// Logs plugin.
import { logManager, API_LOG_TAG } from '../index'
import * as actions from './actions'
import { getLevel, getTypes } from './selectors'

/**
 * The SDK has an internal logging system for providing information about its
 *    behaviour. The SDK will generate logs, at different levels for different
 *    types of information, which are routed to a
 *    "{@link logger.LogHandler Log Handler}" for consumption. An application
 *    can provide their own Log Handler (see
 *    {@link #configconfiglogs config.logs}) to customize how the logs are
 *    handled, or allow the default Log Handler to print the logs to the
 *    console.
 *
 * The SDK's default Log Handler is merely a thin wrapper around the browser's
 *    console API (ie. `window.console`). It receives the log generated by the
 *    SDK, called a "{@link logger.LogEntry Log Entry}", formats a
 *    human-readable message with it, then uses the console to log it at the
 *    appropriate level. This is important to be aware of, since your browser's
 *    console may affect how you see the SDK's default log messages. Since the
 *    default Log Handler uses the console's levels, the browser may filter
 *    which messages are shown depending on which levels it has configured. For
 *    a user that understands console log levels, this can be helpful for
 *    filtering the logs to only the relevant information. But it can equally
 *    be a hindrance by hiding the more detailed log messages (at the 'debug'
 *    level), since browser can have this level hidden by default. For this
 *    reason, we recommend providing a custom Log Handler to the SDK that is
 *    better suited for your application and its users.
 *
 * @public
 * @namespace logger
 * @requires logs
 */

export default function api ({ dispatch, getState }) {
  const log = logManager.getLogger('LOGS')

  const api = {
    /**
     * Possible levels for the SDK logger.
     *
     * The SDK will provide {@link logger.LogEntry Log Entries} to the
     *    {@link logger.LogHandler Log Handler} for all logs at or above the set
     *    log level. 'debug' is considered the lowest level and 'silent' the
     *    highest level. For example, if the current level is 'info', then the
     *    {@link logger.LogHandler Log Handler} will receive
     *    {@link logger.LogEntry Log Entries} for logs at 'info', 'warn', and
     *    'error', but not for the 'debug' level.
     * @public
     * @static
     * @memberof logger
     * @method levels
     * @property {string} SILENT Nothing will be logged.
     * @property {string} ERROR Unhandled error information will be logged. If
     *    the SDK encounters an issue it cannot resolve, the error will be included
     *    in the logs. This likely points to an issue with the SDK itself or an
     *    issue with how the SDK is being used.
     * @property {string} WARN Warning messages for the application developer will
     *    be logged. If the SDK encounters an issue that it can recover and continue,
     *    a warning about the issue will be included in the logs. These logs point
     *    to issues that need to be handled by the application. For example, providing
     *    an invalid configuration to the SDK will cause a warning log that explains
     *    the issue.
     * @property {string} INFO General information about the SDK's operations will
     *    be logged, outlining how the SDK is handling the operations. Reading through
     *    these logs should provide a high-level view of what the SDK is doing,
     *    and why it is doing it.
     * @property {string} DEBUG Detailed information about the SDK's operations,
     *    meant for debugging issues, will be logged. Specific information and relevant
     *    operation data are provided for understanding the scenario that the SDK
     *    was in during the operation.
     */
    levels: {
      SILENT: 'silent',
      ERROR: 'error',
      WARN: 'warn',
      INFO: 'info',
      DEBUG: 'debug'
    },

    /**
     * Update the log level used by the SDK's loggers.
     *
     * Changing the log level will affect the amount of logs that the SDK will
     *    output for debugging purposes.
     *
     * See {@link logger.levels} for valid log levels. Other values will not be
     *    accepted. See the {@link logger.getTypes} API for valid logger types.
     *    Other values will have no affect.
     * @private
     * @static
     * @memberof logger
     * @method setLevel
     * @param  {string} level The log level to set.
     * @param  {string} [type] An optional subset of loggers to update.
     * @example
     * // Set logs to be at the warn level.
     * client.logger.setLevel(client.logger.levels.WARN)
     */
    setLevel (level, type) {
      log.debug(API_LOG_TAG + 'logger.setLevel: ', level, type)

      dispatch(actions.setLevel(level, type))
    },

    /**
     * Get the currently set log level used by the SDK's loggers.
     *
     * @private
     * @static
     * @memberof logger
     * @method getLevel
     * @param  {string} [type] An optional subset of loggers.
     * @return {logger.levels} A log level.
     */
    getLevel (type) {
      log.debug(API_LOG_TAG + 'logger.getLevel: ', type)

      return getLevel(getState(), type)
    },

    /**
     * Updates the {@link logger.LogHandler LogHandler} used by the SDK's
     *    loggers.
     *
     * Changing the log handler will change how the SDK's logs are handled by
     *    the application.
     *
     * See {@link logger.LogHandler} and {@link logger.LogEntry} for information
     *    about the handler. See the {@link logger.getTypes} API for valid
     *    logger types. Other values will have no affect.
     * @private
     * @static
     * @memberof logger
     * @method setHandler
     * @param  {logger.LogEntry} handler A {@link logger.LogHandler} function.
     * @param  {string}   [type] An optional subset of loggers to update.
     * @example
     * // Change the SDK's Log Handler to a custom function.
     * client.logger.setHandler((logEntry) => {
     *    // Handle the SDK's logs in a custom way.
     *    ...
     * })
     *
     */
    setHandler (handler, type) {
      log.debug(API_LOG_TAG + 'logger.setHandler: ', handler, type)

      dispatch(actions.setHandler(handler, type))
    },

    /**
     * Get the list of logger types used by the SDK.
     *
     * Logs for different SDK features can be managed separately from one
     *    another by specifying the type when using the {@link logger.setLevel}
     *    and {@link logger.setHandler} APIs.
     * @private
     * @static
     * @memberof logger
     * @method getTypes
     * @return {Array<string>} Logger types.
     * @example
     * const types = client.logger.getTypes()
     * // types: ['DEFAULT', 'AUTH', 'CALL', ...]
     *
     * // Set the Call logs to be at the debug level.
     * client.logger.setLevel('DEBUG', 'CALL')
     */
    getTypes () {
      log.debug(API_LOG_TAG + 'logger.getTypes')

      return getTypes(getState())
    }
  }

  return {
    logger: api
  }
}
